import { useState, useRef } from 'react';
import { motion } from 'motion/react';
import { getAPIBase } from '../lib/api-fetch';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle,
  Send,
  Building,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { contactSettings, socialMediaSettings } from '../config/brand-config';
import { getContactEmail } from '@/config/runtime-config';

const faqItems = [
  {
    question: 'How do I list my property for sale?',
    answer: 'You can list your property by contacting one of our agents directly, visiting any of our offices, or using the Seller Hub on our website. Our team will guide you through the process, including property valuation, photography, and marketing strategy.',
  },
  {
    question: 'What areas does Something Property cover?',
    answer: 'We primarily operate in Gauteng, covering areas across Johannesburg, Pretoria, and the East Rand. Our network of agents provides extensive coverage across these regions, with offices in Germiston and Pretoria.',
  },
  {
    question: 'How long does the selling process take?',
    answer: 'The timeline varies depending on the property type, location, and market conditions. On average, properties are sold within 60–90 days from listing. Our agents work to market your property effectively to ensure the best possible outcome in a reasonable timeframe.',
  },
  {
    question: 'Do you offer property valuations?',
    answer: 'Yes, we provide free property valuations. Our experienced agents will assess your property based on current market data, comparable sales in the area, and the condition of your home. Contact us to schedule a valuation at your convenience.',
  },
  {
    question: 'What are your commission rates?',
    answer: 'Our commission rates are competitive and in line with industry standards. The exact rate depends on the property value and type of transaction. Please contact us for a detailed quote — we\'re transparent about all costs involved.',
  },
  {
    question: 'How do I schedule a property viewing?',
    answer: 'You can schedule a viewing directly through our website by clicking on a property listing and selecting "Book a Viewing", or by contacting the listed agent via phone, email, or WhatsApp. We also offer virtual tours for select properties.',
  },
  {
    question: 'Can I apply for home loan pre-approval through your site?',
    answer: 'Yes! We offer a Pre-Approval feature on our website where you can check your eligibility and get pre-approved for a home loan. This helps you understand your budget before you start searching for properties.',
  },
  {
    question: 'How do I contact my assigned agent?',
    answer: 'Once an agent is assigned to you, their contact details — including phone, email, and WhatsApp — are available on your property listing page and in the Agent Directory. You can also reach them through the Agent Hub on our website.',
  },
];

export function ContactUs() {
  const faqRef = useRef<HTMLElement>(null);

  const scrollToFaqs = () => {
    faqRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || formData.name;
      const lastName = nameParts.slice(1).join(' ') || '-';
      const response = await fetch(`${getAPIBase()}/public/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName,
          lastName,
          email: formData.email,
          phone: formData.phone,
          type: formData.inquiryType || 'buyer',
          source: 'contact-form',
          notes: `Subject: ${formData.subject}\n\n${formData.message}`,
        }),
      });
      if (!response.ok) throw new Error(`${response.status}`);
      toast.success('Message sent successfully!', {
        description: 'We will get back to you within 24 hours.'
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '', inquiryType: '' });
    } catch {
      toast.error('Failed to send message', {
        description: 'Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const contactEmail = getContactEmail();

  const contactInfo = [
    {
      icon: Phone,
      title: 'Phone',
      value: '011 862 1790 / 012 824 0790',
      link: `tel:0118621790`,
      description: 'Monday - Friday, 8AM - 6PM'
    },
    {
      icon: Mail,
      title: 'Email',
      value: contactEmail,
      link: `mailto:${contactEmail}`,
      description: 'We\'ll respond within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Head Office',
      value: '18 Drakensberg Road, Rondebult',
      link: '#',
      description: 'Germiston, 1424'
    },
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      value: '061 548 3584',
      link: `https://wa.me/27615483584`,
      description: 'Quick response via WhatsApp'
    }
  ];

  const offices = [
    {
      name: 'Head Office - Germiston',
      address: '18 Drakensberg Road, Rondebult',
      city: 'Germiston, 1424',
      phone: '011 862 1790',
      hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM'
    },
    {
      name: 'Pretoria Branch',
      address: 'Flexi Suite, 7th Floor, Suite 6',
      city: 'Menlyn Central Towers, 125 Dallas Ave, Menlyn, Pretoria, 0181',
      phone: '012 824 0790',
      hours: 'Mon-Fri: 8AM-6PM, Sat: 9AM-2PM'
    }
  ];

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(88,101,242,0.08),transparent_50%)]" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6"
            >
              <MessageCircle className="w-4 h-4 text-primary" />
              <span className="text-sm">We're Here to Help</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-br from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              Get In Touch
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Have a question or need assistance? Our team is ready to help you with 
              all your property needs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-12 relative">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* Contact Information Cards */}
            <div className="lg:col-span-1 space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 group">
                    <CardHeader>
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                          <info.icon className="w-6 h-6 text-primary" />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg mb-1">{info.title}</CardTitle>
                          <a 
                            href={info.link}
                            target={info.title === 'WhatsApp' ? '_blank' : undefined}
                            rel={info.title === 'WhatsApp' ? 'noopener noreferrer' : undefined}
                            className="text-sm text-primary hover:underline"
                          >
                            {info.value}
                          </a>
                          <p className="text-xs text-muted-foreground mt-1">{info.description}</p>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-2xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg mb-2">Office Hours</CardTitle>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                          <p>Saturday: 9:00 AM - 2:00 PM</p>
                          <p>Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2"
            >
              <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
                <CardHeader>
                  <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          value={formData.name}
                          onChange={(e) => updateFormData('name', e.target.value)}
                          required
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => updateFormData('email', e.target.value)}
                          required
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => updateFormData('phone', e.target.value)}
                          placeholder="+27 11 123 4567"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inquiryType">Inquiry Type *</Label>
                        <Select 
                          value={formData.inquiryType}
                          onValueChange={(value) => updateFormData('inquiryType', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select inquiry type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="buying">Buying a Property</SelectItem>
                            <SelectItem value="selling">Selling a Property</SelectItem>
                            <SelectItem value="renting">Renting</SelectItem>
                            <SelectItem value="valuation">Property Valuation</SelectItem>
                            <SelectItem value="general">General Inquiry</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => updateFormData('subject', e.target.value)}
                        required
                        placeholder="How can we help you?"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => updateFormData('message', e.target.value)}
                        required
                        placeholder="Tell us more about your inquiry..."
                        rows={6}
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-primary hover:bg-primary/90 group"
                      data-testid="contact-submit"
                      disabled={isSubmitting}
                    >
                      <Send className="w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform" />
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-24 relative bg-muted/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Offices</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Visit us at any of our convenient locations
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {offices.map((office, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="border-border/50 bg-card/50 backdrop-blur-sm hover:border-primary/50 transition-all duration-300 h-full">
                  <CardHeader>
                    <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4">
                      <Building className="w-7 h-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{office.name}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-start gap-2 text-sm">
                      <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-muted-foreground">{office.address}</p>
                        <p className="text-muted-foreground">{office.city}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-primary flex-shrink-0" />
                      <a href={`tel:${office.phone}`} className="text-primary hover:underline">
                        {office.phone}
                      </a>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <Clock className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-muted-foreground">{office.hours}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section ref={faqRef} id="faqs" className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-6">
              <HelpCircle className="w-4 h-4 text-primary" />
              <span className="text-sm">FAQs</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Find answers to the most common questions about buying, selling, and renting properties with Something Property.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
              <CardContent className="p-6 md:p-8">
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((item, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-base text-left hover:no-underline hover:text-primary">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-24 relative">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Still Have Questions?
            </h2>
            <p className="text-xl text-muted-foreground mb-10">
              Check out our frequently asked questions or reach out to us directly
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                variant="outline"
                className="px-10 py-7 text-lg border-border/50 hover:border-primary/50 hover:bg-primary/5"
                data-testid="contact-view-faqs"
                onClick={scrollToFaqs}
              >
                View FAQs
              </Button>
              <Button
                size="lg"
                onClick={() => {
                  const whatsappNumber = (socialMediaSettings.whatsapp || '+27111234567').replace(/[^0-9]/g, '');
                  window.open(`https://wa.me/${whatsappNumber}`, '_blank');
                }}
                className="px-10 py-7 text-lg bg-green-600 hover:bg-green-700 text-white"
                data-testid="contact-whatsapp"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Chat on WhatsApp
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}